import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { LucideAngularModule, RotateCw, Trash2, X, Crown, Eraser, LayoutTemplate as LayoutTemplateIcon, ChevronDown, ChevronRight, Save, Eye, Check, Armchair, Pencil, Plus, Minus } from 'lucide-angular';
import { TablePlanService } from '../table-plan.service';
import {
  TablePlan,
  TablePlanGuest,
  TablePlanMarker,
  TablePlanMarkerType,
  TablePlanSeat,
  TablePlanSummary,
  TablePlanTable,
  TablePlanTileType,
  TablePlanVenueTemplate,
} from '../table-plan.model';
import { GeneratedTable } from './layout/layout-generator';
import { LayoutPreviewComponent } from './layout/layout-preview.component';
import {
  CHAIR_D,
  CHAIR_W,
  DEFAULT_FURNITURE,
  FurnitureSettings,
  GRID_STEP,
  PlacedBox,
  SIDES,
  Side,
  SideCounts,
  TILE_LABEL,
  TILE_TYPES,
  TRESTLE_LONG,
  TileType,
  allowedSides,
  chairRect,
  clampToCanvas,
  defaultSideCounts,
  footprint,
  isRun,
  isSquare,
  isTopTableType,
  isVertical,
  previewPiece,
  rotatedTableSize,
  sideCountsOf,
  sideMax,
  snapToGrid,
  tableBox,
  tableSize,
  tableTransform,
  toLocal,
  tooClose,
  trestleCount,
  trestleRects,
  worldAxis,
  worldSide,
} from './canvas-geometry';
import {
  RoomOutline,
  boxFitsInRoom,
  outlineForVenue,
  NO_ROOM_OUTLINE,
} from './room-outlines/rooms';

/** A chair drawn on a table: its seat and its rect in table-local space. */
interface PlacedChair {
  seat: TablePlanSeat;
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
  orientation: 'horizontal' | 'vertical';
  high: boolean;
}

/** Where a dragged chair would land: a side of a table, at a place along it. */
interface SideTarget {
  tableId: number;
  side: Side;
  index: number;
}

/** A resolved drop: the spot a table lands on, or a table it joins. */
interface PlacementResult {
  x: number;
  y: number;
  valid: boolean;
  joinTargetId: number | null;
}

/**
 * TVG-103 / TVG-142: Drag-to-arrange room view of the Table Plan. Renders the
 * venue's own room outline true-to-scale (1 canvas unit = 1 cm), the placed
 * tile catalogue, and the guest sidebar. The canvas reads and writes the same
 * plan the TVG-98 panel uses — both views stay in sync via the parent's `plan`
 * input and the `change` output.
 *
 * Interaction summary:
 *  - Furniture → canvas: drag a trestle, square or top table onto the room.
 *    TVG-373: everything is true size, and a drop outside the walls or into
 *    another table's walking space springs back with a red outline.
 *  - Placed table → canvas: drag to move. Tables snap together edge to edge
 *    on sides with no chairs; a trestle dropped on the end of a run of the same
 *    kind joins it, and two single trestles side by side make a square.
 *  - Table selected: floating bar offers Rotate / + trestle / − trestle / Top
 *    table / Delete.
 *  - Chairs: drag one on from Furniture onto any side, drag one off to remove
 *    it; hovering a chair opens its menu (high chair, remove, clear guest).
 *  - Guest sidebar → chair: drag to assign (re-seating clears the source).
 *
 * `interactive=false` switches to view-only — no drag/select handlers fire.
 */
@Component({
  selector: 'app-table-plan-canvas',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, LayoutPreviewComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './table-plan-canvas.component.html',
  styleUrl: './table-plan-canvas.component.css',
})
export class TablePlanCanvasComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) public eventId!: number | string;
  @Input({ required: true }) public plan!: TablePlan;
  @Input({ required: true }) public summary!: TablePlanSummary;
  @Input() public guests: TablePlanGuest[] = [];
  @Input() public interactive = true;
  /** Venue name, used to pick the room outline. */
  @Input() public venueName: string | null = null;
  /**
   * TVG-142: pin the canvas pane to the viewport (sticky) so the room and
   * status strip stay on screen while the page scrolls. Enabled in the
   * canvas-only view; disabled in "Both" view, where the full-width editing
   * panels sit below the canvas and a pinned pane would paint over them. The
   * guest list scrolls inside its own column either way.
   */
  @Input() public pinned = true;

  /** Guest count, shown against each template's seat total for comparison. */
  @Input() public guestCount = 0;
  /**
   * The `layout_template_key` marker on the plan. Since TVG-189 this holds the
   * `venue-template:{id}` marker of the saved template selected for this event
   * (the auto-fill layout gallery was retired), or null when none is selected.
   */
  @Input() public chosenLayoutKey: string | null = null;

  /**
   * TVG-189: saved per-venue templates, listed in the left editor pane. Each
   * carries its geometry so it renders a thumbnail and a ghost preview; Preview
   * shows it on the canvas and Select locks it in. The parent owns the
   * save/load/delete round-trips and re-feeds this list.
   */
  @Input() public venueTemplates: TablePlanVenueTemplate[] = [];

  /** TVG-373: seats per trestle side and walking space, from the settings. */
  @Input() public furniture: FurnitureSettings = DEFAULT_FURNITURE;

  @HostBinding('class.tp-canvas-pinned') protected get isPinned(): boolean {
    return this.pinned;
  }

  @Output() public change = new EventEmitter<void>();
  @Output() public error = new EventEmitter<string>();
  /**
   * TVG-189: save/load/delete of per-venue saved templates. The parent performs
   * the API round-trip (it owns the plan reload) and re-feeds `venueTemplates`.
   * `saveTemplate` carries the name typed into the in-pane form; `loadTemplate`
   * is the Select action — it locks the template in for the event.
   */
  @Output() public saveTemplate = new EventEmitter<string>();
  @Output() public loadTemplate = new EventEmitter<TablePlanVenueTemplate>();
  @Output() public deleteTemplate = new EventEmitter<TablePlanVenueTemplate>();
  /**
   * TVG-227: edit an existing saved template. `renameTemplate` carries the new
   * name typed into the card's inline field; `updateTemplateLayout` asks the
   * parent to overwrite the template's stored arrangement with the current
   * canvas. The parent owns the API round-trip and re-feeds `venueTemplates`.
   */
  @Output() public renameTemplate = new EventEmitter<{ template: TablePlanVenueTemplate; name: string }>();
  @Output() public updateTemplateLayout = new EventEmitter<TablePlanVenueTemplate>();
  /**
   * TVG-142: a click (not a drag) on a guest tile asks to open that guest's
   * record — for example to set the high chair flag or view dietary details.
   * Dragging the same tile onto a chair still assigns the guest.
   */
  @Output() public openGuest = new EventEmitter<number>();

  @ViewChild('svg', { static: true }) private svgRef!: ElementRef<SVGSVGElement>;
  /** TVG-145: the root grid, sized to the visible area so the canvas never grows the page. */
  @ViewChild('grid', { static: true }) private gridRef!: ElementRef<HTMLElement>;
  /** TVG-189: the "new template name" field, focused when the save flow opens. */
  @ViewChild('templateNameInput') private templateNameInput?: ElementRef<HTMLInputElement>;
  /** TVG-227: the inline "rename template" field, focused when a rename starts. */
  @ViewChild('renameInput') private renameInput?: ElementRef<HTMLInputElement>;

  /** TVG-189: name being typed for a new saved template. */
  protected newTemplateName = '';

  /** TVG-227: id of the template whose name is being edited inline, or null. */
  protected renamingTemplateId: number | null = null;
  /** TVG-227: draft name held while an inline rename is open. */
  protected renameDraft = '';

  protected readonly RotateIcon = RotateCw;
  protected readonly TrashIcon = Trash2;
  protected readonly CloseIcon = X;
  protected readonly CrownIcon = Crown;
  protected readonly EraserIcon = Eraser;
  protected readonly LayoutIcon = LayoutTemplateIcon;
  protected readonly FurnitureIcon = Armchair;
  protected readonly ChevronDownIcon = ChevronDown;
  protected readonly ChevronRightIcon = ChevronRight;
  protected readonly SaveIcon = Save;
  protected readonly PreviewIcon = Eye;
  protected readonly SelectIcon = Check;
  protected readonly RenameIcon = Pencil;
  protected readonly PlusIcon = Plus;
  protected readonly MinusIcon = Minus;

  protected readonly GRID_STEP = GRID_STEP;
  protected readonly CHAIR_W = CHAIR_W;
  protected readonly CHAIR_D = CHAIR_D;
  protected readonly TILE_TYPES = TILE_TYPES;
  protected readonly TILE_LABEL = TILE_LABEL;

  /**
   * TVG-373: plan colours agreed on 26 October 2026. They apply to the plan
   * only (room, tables, chairs and their marks); the portal keeps its own.
   */
  protected readonly PLAN_COLOURS = {
    wall: '#2A332E',
    grid: '#EEF1F4',
    table: '#E3ECE6',
    tableEdge: '#3F6B55',
    emptyChair: '#FFFFFF',
    emptyChairEdge: '#9AA3B2',
    seatedChair: '#1F4D3A',
    dietary: '#D97706',
    allergy: '#DC2626',
    highChair: '#7C3AED',
    selected: '#3F6B55',
    tooClose: '#DC2626',
  } as const;

  /** The key shown above the plan. */
  protected readonly PLAN_KEY: ReadonlyArray<{ label: string; kind: 'empty' | 'seated' | 'dietary' | 'allergy' | 'high' }> = [
    { label: 'Empty', kind: 'empty' },
    { label: 'Seated', kind: 'seated' },
    { label: 'Dietary', kind: 'dietary' },
    { label: 'Allergy', kind: 'allergy' },
    { label: 'High chair', kind: 'high' },
  ];

  /** How close (cm) a drop must be to snap edge to edge or to join a run. */
  private static readonly SNAP_DISTANCE = 40;
  private static readonly JOIN_DISTANCE = 120;

  /** Table outlined red for a moment after a blocked drop. */
  protected flashTableId: number | null = null;
  private flashTimer: ReturnType<typeof setTimeout> | null = null;

  /** Margin (cm) around the room bbox so wall labels and flush tiles stay in view. */
  protected readonly MARGIN = 90;

  protected selectedTableId: number | null = null;
  protected hoveredSeatId: number | null = null;
  protected saving = false;
  /**
   * TVG-189: the left pane is an accordion of two sections — Templates and
   * Furniture. Opening one collapses the other; both can be collapsed.
   * TVG-379: Furniture opens first. With Templates open the only add control
   * on screen was the status strip's "Add chair", so the runs, top tables and
   * walking space looked missing.
   */
  protected expandedSection: 'templates' | 'furniture' | null = 'furniture';

  /** TVG-379: shade the walking space round every table, not just the one in hand. */
  protected showWalkingSpace = false;

  /**
   * TVG-189: id of the saved template currently ghost-previewed on the canvas,
   * or null when nothing is being previewed. Select locks a template in and is
   * tracked server-side via `chosenLayoutKey`.
   */
  protected previewTemplateId: number | null = null;

  /** Active in-canvas drag (move existing tile). */
  protected dragState: {
    mode: 'move-tile';
    tableId: number;
    pointerStart: { x: number; y: number };
    tableStart: { x: number; y: number };
    current: { x: number; y: number };
    /** TVG-373: where the drop would land (snapped / joined) and if it is allowed. */
    placement: PlacementResult | null;
  } | null = null;

  /** Active catalogue → canvas drag (place new tile). */
  protected catalogueDrag: {
    tileType: TileType;
    pointer: { x: number; y: number };
    overCanvas: boolean;
  } | null = null;

  /** Active guest → chair drag (assign), or a click to open the guest record. */
  protected guestDrag: {
    guest: TablePlanGuest;
    start: { x: number; y: number };
    pointer: { x: number; y: number };
    moved: boolean;
    overSeatId: number | null;
  } | null = null;

  protected chairDrag: {
    mode: 'move' | 'add';
    seat: TablePlanSeat | null;
    sourceTableId: number | null;
    start: { x: number; y: number };
    pointer: { x: number; y: number };
    moved: boolean;
    overSide: SideTarget | null;
  } | null = null;

  private static readonly CHAIR_DRAG_THRESHOLD = 4;

  // ----- TVG-216: fixed-area markers (bar / dance floor / toilet) -----

  /** id of the marker currently selected on the canvas, or null. */
  protected selectedMarkerId: number | null = null;

  /** The marker kinds shown in the palette, with their default box footprint. */
  protected readonly MARKER_CATALOGUE: ReadonlyArray<{
    type: TablePlanMarkerType;
    label: string;
    width: number;
    height: number;
  }> = [
    { type: 'bar', label: 'Bar', width: 300, height: 100 },
    { type: 'dance_floor', label: 'Dance floor', width: 400, height: 400 },
    { type: 'toilet', label: 'Toilets', width: 250, height: 200 },
  ];

  /** Per-type box styling so each area reads distinctly on the canvas. */
  protected readonly MARKER_STYLE: Record<TablePlanMarkerType, { fill: string; stroke: string; text: string }> = {
    bar: { fill: '#fef3c7', stroke: '#d97706', text: '#92400e' },
    dance_floor: { fill: '#ede9fe', stroke: '#7c3aed', text: '#5b21b6' },
    toilet: { fill: '#e0f2fe', stroke: '#0284c7', text: '#075985' },
  };

  /** Smallest a marker box may be dragged to, in canvas cm (matches server). */
  private static readonly MARKER_MIN_SIZE = 50;

  /** Active in-canvas drag to move an existing marker. */
  protected markerDrag: {
    markerId: number;
    pointerStart: { x: number; y: number };
    markerStart: { x: number; y: number };
    current: { x: number; y: number };
  } | null = null;

  /** Active bottom-right-corner drag to resize the selected marker. */
  protected markerResize: {
    markerId: number;
    pointerStart: { x: number; y: number };
    sizeStart: { w: number; h: number };
    current: { w: number; h: number };
  } | null = null;

  /** Active palette → canvas drag to place a new marker. */
  protected markerCatalogueDrag: {
    type: TablePlanMarkerType;
    label: string;
    width: number;
    height: number;
    pointer: { x: number; y: number };
    overCanvas: boolean;
  } | null = null;

  constructor(
    private tablePlanService: TablePlanService,
    private cdr: ChangeDetectorRef,
  ) {}

  // ----- Canvas box sizing (TVG-145) -----

  /**
   * TVG-145: the app shell scrolls inside an inner `overflow-y-auto` container,
   * not the document, so we size the canvas box in JS off that container rather
   * than `100vh`.
   *
   * Refinement (TVG-145, follow-up): the first cut sized the box to the area
   * *below* the summary cards (container height minus the canvas' own offset),
   * which left the room tiny in a mostly-empty box — in the canvas-only view the
   * pane is pinned (sticky, `top: 1rem`) so those cards scroll away and the box
   * could use the whole viewport. We now size the box to the full visible height
   * of the scroll container: minus the sticky gap when pinned, minus nothing in
   * "Both" view (where it sits in flow). The room (true-to-scale since TVG-142)
   * is still meet-scaled to fit, so it scales up to fill the available space
   * while the canvas and status strip stay put and only the guest list scrolls.
   */
  private resizeObserver: ResizeObserver | null = null;
  private boxUpdateScheduled = false;
  private static readonly CANVAS_BOX_GUTTER = 24;
  private static readonly CANVAS_BOX_MIN = 360;
  private static readonly LG_BREAKPOINT = 1024;
  /** Matches the sticky offset on `:host.tp-canvas-pinned` (top: 1rem). */
  private static readonly STICKY_TOP = 16;

  ngAfterViewInit(): void {
    this.scheduleCanvasBoxUpdate();
    // Recompute when the viewport (and thus the scroll container) resizes.
    const scrollParent = this.scrollParentOf(this.gridRef?.nativeElement ?? null);
    if (scrollParent && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleCanvasBoxUpdate());
      this.resizeObserver.observe(scrollParent);
    }
  }

  ngOnChanges(): void {
    // Inputs changing (e.g. the summary toggling the advisory banner above us)
    // can shift the canvas' offset, so re-measure after the view settles.
    this.scheduleCanvasBoxUpdate();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.flashTimer) {
      clearTimeout(this.flashTimer);
    }
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    this.scheduleCanvasBoxUpdate();
  }

  private scheduleCanvasBoxUpdate(): void {
    if (this.boxUpdateScheduled || typeof requestAnimationFrame === 'undefined') {
      if (typeof requestAnimationFrame === 'undefined') {
        this.updateCanvasBox();
      }
      return;
    }
    this.boxUpdateScheduled = true;
    requestAnimationFrame(() => {
      this.boxUpdateScheduled = false;
      this.updateCanvasBox();
    });
  }

  private updateCanvasBox(): void {
    const grid = this.gridRef?.nativeElement;
    if (!grid || typeof window === 'undefined') {
      return;
    }
    // Below lg the layout stacks and the page scrolls naturally — no fixed box.
    if (window.innerWidth < TablePlanCanvasComponent.LG_BREAKPOINT) {
      grid.style.removeProperty('--tp-canvas-box');
      return;
    }
    const scrollParent = this.scrollParentOf(grid);
    if (!scrollParent) {
      return;
    }
    // Fill the full visible height of the scroll container so the room scales up
    // to use the available space. When pinned the pane sticks at `top: 1rem`
    // (summary cards scroll away above it), so reserve that gap; in "Both" view
    // it sits in flow and takes a near-full-viewport box. The grid stays a fixed
    // box with the guest list scrolling inside it, so the lock still holds.
    const topGap = this.pinned ? TablePlanCanvasComponent.STICKY_TOP : 0;
    const available = Math.max(
      TablePlanCanvasComponent.CANVAS_BOX_MIN,
      scrollParent.clientHeight - topGap - TablePlanCanvasComponent.CANVAS_BOX_GUTTER,
    );
    grid.style.setProperty('--tp-canvas-box', `${Math.round(available)}px`);
  }

  /** Nearest scrollable ancestor, falling back to the document scroller. */
  private scrollParentOf(element: HTMLElement | null): HTMLElement | null {
    let node = element?.parentElement ?? null;
    while (node) {
      const style = getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(style.overflowY)) {
        return node;
      }
      node = node.parentElement;
    }
    return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
  }

  // ----- Room / scale (TVG-142) -----

  protected get outline(): RoomOutline {
    // TVG-372: no Milling Barn fallback. The tab is hidden for a venue with no
    // room, so the empty outline is never shown to staff.
    return outlineForVenue(this.venueName) ?? NO_ROOM_OUTLINE;
  }

  // ----- TVG-189: saved-template preview & select in the left editor pane -----

  /**
   * The stored tables of a saved template, as the preview/thumbnail geometry.
   * The shape matches `GeneratedTable`, so it feeds the shared preview renderer.
   */
  protected tablesForTemplate(template: TablePlanVenueTemplate): GeneratedTable[] {
    return template.tables as unknown as GeneratedTable[];
  }

  /** How many seats a template carries beyond the event's guest count. */
  protected templateSurplus(template: TablePlanVenueTemplate): number {
    return Math.max(0, template.seat_total - this.guestCount);
  }

  /** The `layout_template_key` marker a given saved template locks in as. */
  protected venueTemplateKey(template: TablePlanVenueTemplate): string {
    return `venue-template:${template.id}`;
  }

  /** True when this template is the one currently selected for the event. */
  protected isSelectedTemplate(template: TablePlanVenueTemplate): boolean {
    return this.chosenLayoutKey === this.venueTemplateKey(template);
  }

  /** True once any saved template has been selected/locked for the event. */
  protected get hasSelectedTemplate(): boolean {
    return this.chosenLayoutKey?.startsWith('venue-template:') ?? false;
  }

  protected isPreviewingTemplate(template: TablePlanVenueTemplate): boolean {
    return this.previewTemplateId === template.id;
  }

  protected get isPreviewing(): boolean {
    return this.previewTemplateId !== null;
  }

  protected get previewTemplate(): TablePlanVenueTemplate | null {
    if (this.previewTemplateId === null) {
      return null;
    }
    return this.venueTemplates.find((t) => t.id === this.previewTemplateId) ?? null;
  }

  /** Ghost tables rendered on the canvas while a template is being previewed. */
  protected get previewTables(): GeneratedTable[] {
    const template = this.previewTemplate;
    return template ? this.tablesForTemplate(template) : [];
  }

  /** TVG-216: ghost markers rendered while a template is being previewed. */
  protected get previewMarkers(): TablePlanMarker[] {
    return (this.previewTemplate?.markers ?? []).map((m, i) => ({
      id: -(i + 1),
      type: m.type,
      label: m.label,
      position_x: m.position_x,
      position_y: m.position_y,
      width: m.width,
      height: m.height,
      sort_order: i,
    }));
  }

  /**
   * Preview a saved template on the canvas (a non-committal ghost). Previewing
   * the one already shown clears it.
   */
  protected previewVenueTemplate(template: TablePlanVenueTemplate): void {
    if (!this.interactive) {
      return;
    }
    this.previewTemplateId = this.previewTemplateId === template.id ? null : template.id;
    this.selectedTableId = null;
    this.cdr.markForCheck();
  }

  protected cancelPreview(): void {
    this.previewTemplateId = null;
    this.cdr.markForCheck();
  }

  /**
   * Select a saved template — lock it in as the event's template. Hands it up to
   * the parent to persist, clears the preview, and swaps the accordion to
   * Furniture (the next thing the user edits). The Templates header stays
   * reopenable so a different template can still be chosen.
   */
  protected selectVenueTemplate(template: TablePlanVenueTemplate): void {
    if (!this.interactive) {
      return;
    }
    this.loadTemplate.emit(template);
    this.previewTemplateId = null;
    this.expandedSection = 'furniture';
    this.cdr.markForCheck();
  }

  /** Select the template currently being previewed. */
  protected applyPreview(): void {
    const template = this.previewTemplate;
    if (!template || this.saving) {
      return;
    }
    this.selectVenueTemplate(template);
  }

  /** A ghost-preview table drawn at true size (TVG-373). */
  protected previewPieceFor(table: GeneratedTable) {
    return previewPiece(table);
  }

  /**
   * Grid columns: the accordion pane + canvas + guests when interactive, canvas
   * + guests in view-only mode. The literal Tailwind values appear here so the
   * JIT compiler emits them.
   */
  protected get gridColsClass(): string {
    return this.interactive
      ? 'lg:grid-cols-[168px_1fr_240px]'
      : 'lg:grid-cols-[1fr_240px]';
  }

  /** SVG viewBox sized to the room bbox plus a label margin. */
  protected get viewBox(): string {
    const o = this.outline;
    return `${-this.MARGIN} ${-this.MARGIN} ${o.width + 2 * this.MARGIN} ${o.height + 2 * this.MARGIN}`;
  }

  private get viewBoxWidth(): number {
    return this.outline.width + 2 * this.MARGIN;
  }

  private get viewBoxHeight(): number {
    return this.outline.height + 2 * this.MARGIN;
  }

  protected get hasUnplacedTables(): boolean {
    return (this.plan?.tables ?? []).some((t) => t.position_x === 0 && t.position_y === 0);
  }

  protected get unplacedTables(): TablePlanTable[] {
    return (this.plan?.tables ?? []).filter((t) => t.position_x === 0 && t.position_y === 0);
  }

  protected get placedTables(): TablePlanTable[] {
    return (this.plan?.tables ?? []).filter((t) => t.position_x !== 0 || t.position_y !== 0);
  }

  protected get selectedTable(): TablePlanTable | null {
    if (this.selectedTableId === null) {
      return null;
    }
    return this.plan?.tables.find((t) => t.id === this.selectedTableId) ?? null;
  }

  protected get hasTables(): boolean {
    return (this.plan?.tables?.length ?? 0) > 0;
  }

  // ----- TVG-216: markers on the canvas -----

  protected get markers(): TablePlanMarker[] {
    return this.plan?.markers ?? [];
  }

  protected get selectedMarker(): TablePlanMarker | null {
    if (this.selectedMarkerId === null) {
      return null;
    }
    return this.markers.find((m) => m.id === this.selectedMarkerId) ?? null;
  }

  /**
   * The live box for a marker (x, y, w, h), reflecting any in-flight move or
   * resize so the rect follows the pointer before the change is persisted.
   */
  protected markerBox(marker: TablePlanMarker): { x: number; y: number; w: number; h: number } {
    let x = marker.position_x;
    let y = marker.position_y;
    let w = marker.width;
    let h = marker.height;
    if (this.markerDrag && this.markerDrag.markerId === marker.id) {
      x = this.markerDrag.current.x;
      y = this.markerDrag.current.y;
    }
    if (this.markerResize && this.markerResize.markerId === marker.id) {
      w = this.markerResize.current.w;
      h = this.markerResize.current.h;
    }
    return { x, y, w, h };
  }

  protected markerLabelFor(type: TablePlanMarkerType): string {
    return this.MARKER_CATALOGUE.find((m) => m.type === type)?.label ?? type;
  }

  protected trackByMarkerType(_index: number, item: { type: TablePlanMarkerType }): string {
    return item.type;
  }

  /** Screen position for the selected marker's floating toolbar. */
  protected markerToolbarPosition(marker: TablePlanMarker): { left: number; top: number } | null {
    const projection = this.svgProjection();
    if (!projection) {
      return null;
    }
    const { rect, scale, offsetX, offsetY } = projection;
    const box = this.markerBox(marker);
    return {
      left: rect.left + offsetX + (box.x + box.w + this.MARGIN) * scale + 8,
      top: rect.top + offsetY + (box.y + this.MARGIN) * scale,
    };
  }

  protected trackById(_index: number, item: { id: number }): number {
    return item.id;
  }

  protected trackByTileType(_index: number, type: TileType): string {
    return type;
  }

  protected trackByGuest(_index: number, guest: TablePlanGuest): number {
    return guest.id;
  }

  protected initialsFor(guest: TablePlanGuest | null): string {
    if (!guest) {
      return '';
    }
    const first = (guest.first_name ?? '').trim().charAt(0).toUpperCase();
    const last = (guest.last_name ?? '').trim().charAt(0).toUpperCase();
    return (first + last) || (guest.full_name ?? '').trim().charAt(0).toUpperCase();
  }

  protected isGuestSeated(guestId: number): boolean {
    return (this.plan?.tables ?? []).some((t) =>
      t.seats.some((s) => s.event_guest_id === guestId),
    );
  }

  // ----- TVG-143: guest list filter + placement columns -----

  protected guestFilter: 'all' | 'seated' | 'unseated' = 'all';

  /** TVG-142: type-to-search term to filter a long guest list by name. */
  protected guestSearch = '';

  protected setGuestFilter(filter: 'all' | 'seated' | 'unseated'): void {
    this.guestFilter = filter;
  }

  protected setGuestSearch(value: string): void {
    this.guestSearch = value;
    this.cdr.markForCheck();
  }

  protected get seatedCount(): number {
    return this.guests.filter((g) => this.isGuestSeated(g.id)).length;
  }

  protected get unseatedCount(): number {
    return this.guests.length - this.seatedCount;
  }

  protected get filteredGuests(): TablePlanGuest[] {
    let list = this.guests;
    if (this.guestFilter === 'seated') {
      list = list.filter((g) => this.isGuestSeated(g.id));
    } else if (this.guestFilter === 'unseated') {
      list = list.filter((g) => !this.isGuestSeated(g.id));
    }
    // TVG-142: type-to-search across the guest's name so a long list can be
    // narrowed quickly. Matches the full name and either name part.
    const term = this.guestSearch.trim().toLowerCase();
    if (term) {
      list = list.filter(
        (g) =>
          (g.full_name ?? '').toLowerCase().includes(term) ||
          (g.first_name ?? '').toLowerCase().includes(term) ||
          (g.last_name ?? '').toLowerCase().includes(term),
      );
    }
    return list;
  }

  private tableNumberMap(): Map<number, number> {
    const ordered = [...(this.plan?.tables ?? [])].sort(
      (a, b) => a.sort_order - b.sort_order || a.id - b.id,
    );
    const map = new Map<number, number>();
    ordered.forEach((t, i) => map.set(t.id, i + 1));
    return map;
  }

  protected guestPlacement(guestId: number): { table: number; seat: number } | null {
    const numbers = this.tableNumberMap();
    for (const table of this.plan?.tables ?? []) {
      const seat = table.seats.find((s) => s.event_guest_id === guestId);
      if (seat) {
        return { table: numbers.get(table.id) ?? 0, seat: seat.seat_number };
      }
    }
    return null;
  }

  // ----- TVG-143: always-present status strip -----

  protected get placedGuestCount(): number {
    return this.summary?.assigned_count ?? this.seatedCount;
  }

  protected get totalGuestCount(): number {
    return this.summary?.guest_count ?? this.guests.length;
  }

  protected get placementMismatch(): boolean {
    return this.placedGuestCount !== this.totalGuestCount;
  }

  // ----- Catalogue (TVG-142) -----

  /**
   * TVG-189: accordion toggle — open the given section (which collapses the
   * other) or, if it is already open, collapse it. Both sections can be closed.
   */
  protected toggleSection(section: 'templates' | 'furniture'): void {
    this.expandedSection = this.expandedSection === section ? null : section;
    this.cdr.markForCheck();
  }

  /**
   * TVG-189: reveal the save-as-template form — open the Templates section and
   * focus the name field. Called from the always-visible status-strip button so
   * the save flow is reachable while the user is placing furniture.
   */
  protected openSaveTemplate(): void {
    if (!this.interactive) {
      return;
    }
    if (!this.hasTables) {
      this.error.emit('Add at least one table to the canvas before saving it as a template.');
      return;
    }
    this.expandedSection = 'templates';
    this.cdr.markForCheck();
    // The name field only exists once the section renders, so focus next tick.
    setTimeout(() => this.templateNameInput?.nativeElement.focus(), 0);
  }

  /**
   * TVG-189: save the current canvas as a new named template. Uses the in-pane
   * name field (no browser prompt, which some environments suppress) and hands
   * the name up to the parent, which performs the API save and reloads the list.
   */
  protected submitNewTemplate(): void {
    if (!this.interactive) {
      return;
    }
    if (!this.hasTables) {
      this.error.emit('Add at least one table to the canvas before saving it as a template.');
      return;
    }
    const name = this.newTemplateName.trim();
    if (!name) {
      this.error.emit('Give the new template a name first.');
      this.templateNameInput?.nativeElement.focus();
      return;
    }
    this.saveTemplate.emit(name);
    this.newTemplateName = '';
  }

  protected emitDeleteTemplate(template: TablePlanVenueTemplate): void {
    if (!this.interactive) {
      return;
    }
    this.deleteTemplate.emit(template);
  }

  // ----- TVG-227: rename a saved template & re-save its layout -----

  protected isRenaming(template: TablePlanVenueTemplate): boolean {
    return this.renamingTemplateId === template.id;
  }

  /** Open the inline rename field for a template, seeded with its current name. */
  protected startRename(template: TablePlanVenueTemplate): void {
    if (!this.interactive) {
      return;
    }
    this.renamingTemplateId = template.id;
    this.renameDraft = template.name;
    this.cdr.markForCheck();
    // The field only exists once the card re-renders, so focus next tick.
    setTimeout(() => this.renameInput?.nativeElement.focus(), 0);
  }

  protected cancelRename(): void {
    this.renamingTemplateId = null;
    this.renameDraft = '';
    this.cdr.markForCheck();
  }

  /**
   * Commit the inline rename — hands the trimmed name up to the parent, which
   * performs the API call and reloads the list. A blank or unchanged name just
   * closes the field.
   */
  protected submitRename(template: TablePlanVenueTemplate): void {
    if (!this.interactive) {
      return;
    }
    const name = this.renameDraft.trim();
    if (!name || name === template.name) {
      this.cancelRename();
      return;
    }
    this.renameTemplate.emit({ template, name });
    this.cancelRename();
  }

  /**
   * Ask the parent to overwrite this template's stored layout with the current
   * canvas — the "save my edits back to the same template" action, offered on
   * the template selected for the event.
   */
  protected emitUpdateLayout(template: TablePlanVenueTemplate): void {
    if (!this.interactive) {
      return;
    }
    if (!this.hasTables) {
      this.error.emit('Add at least one table to the canvas before saving it to a template.');
      return;
    }
    this.updateTemplateLayout.emit(template);
  }

  protected startCatalogueDrag(event: PointerEvent, tileType: TileType): void {
    if (!this.interactive) {
      return;
    }
    event.preventDefault();
    this.catalogueDrag = {
      tileType,
      pointer: { x: event.clientX, y: event.clientY },
      overCanvas: false,
    };
  }

  // ----- TVG-216: marker place / move / resize -----

  /** Start dragging a fixed-area marker out of the palette onto the room. */
  protected startMarkerCatalogueDrag(
    event: PointerEvent,
    item: { type: TablePlanMarkerType; label: string; width: number; height: number },
  ): void {
    if (!this.interactive) {
      return;
    }
    event.preventDefault();
    this.markerCatalogueDrag = {
      type: item.type,
      label: item.label,
      width: item.width,
      height: item.height,
      pointer: { x: event.clientX, y: event.clientY },
      overCanvas: false,
    };
  }

  /** Start moving a placed marker. */
  protected startMarkerDrag(event: PointerEvent, marker: TablePlanMarker): void {
    if (!this.interactive) {
      return;
    }
    event.stopPropagation();
    this.selectMarkerId(marker.id);
    this.markerDrag = {
      markerId: marker.id,
      pointerStart: { x: event.clientX, y: event.clientY },
      markerStart: { x: marker.position_x, y: marker.position_y },
      current: { x: marker.position_x, y: marker.position_y },
    };
  }

  /** Start resizing the selected marker from its bottom-right corner handle. */
  protected startMarkerResize(event: PointerEvent, marker: TablePlanMarker): void {
    if (!this.interactive) {
      return;
    }
    event.stopPropagation();
    this.selectMarkerId(marker.id);
    this.markerResize = {
      markerId: marker.id,
      pointerStart: { x: event.clientX, y: event.clientY },
      sizeStart: { w: marker.width, h: marker.height },
      current: { w: marker.width, h: marker.height },
    };
  }

  protected selectMarker(event: Event, marker: TablePlanMarker): void {
    event.stopPropagation();
    this.selectMarkerId(marker.id);
  }

  private selectMarkerId(id: number): void {
    this.selectedMarkerId = id;
    this.selectedTableId = null;
    this.cdr.markForCheck();
  }

  protected closeMarkerSelection(): void {
    this.selectedMarkerId = null;
    this.cdr.markForCheck();
  }

  protected deleteSelectedMarker(): void {
    const marker = this.selectedMarker;
    if (!marker || !this.interactive) {
      return;
    }
    if (!confirm(`Remove the "${marker.label}" area from the canvas?`)) {
      return;
    }
    const idx = this.markers.findIndex((m) => m.id === marker.id);
    if (idx < 0) {
      return;
    }
    const removed = this.plan.markers.splice(idx, 1)[0];
    this.selectedMarkerId = null;
    this.saving = true;
    this.change.emit();
    this.cdr.markForCheck();
    if (marker.id < 0) {
      // Never persisted (drop still in flight) — the optimistic removal is enough.
      this.saving = false;
      return;
    }
    this.tablePlanService.deleteMarker(this.eventId, marker.id).subscribe({
      next: () => {
        this.saving = false;
      },
      error: () => {
        this.plan.markers.splice(idx, 0, removed);
        this.change.emit();
        this.fail('Failed to remove the area.');
      },
    });
  }

  // ----- Tile move drag -----

  protected startTileDrag(event: PointerEvent, table: TablePlanTable): void {
    if (!this.interactive) {
      return;
    }
    event.stopPropagation();
    this.selectedTableId = table.id;

    this.dragState = {
      mode: 'move-tile',
      tableId: table.id,
      pointerStart: { x: event.clientX, y: event.clientY },
      tableStart: { x: table.position_x, y: table.position_y },
      current: { x: table.position_x, y: table.position_y },
      placement: null,
    };
  }

  // ----- Guest drag -----

  protected startGuestDrag(event: PointerEvent, guest: TablePlanGuest): void {
    if (!this.interactive) {
      return;
    }
    event.preventDefault();
    this.guestDrag = {
      guest,
      start: { x: event.clientX, y: event.clientY },
      pointer: { x: event.clientX, y: event.clientY },
      moved: false,
      overSeatId: null,
    };
  }

  // ----- Chair drag (TVG-132) -----

  protected startChairDrag(event: PointerEvent, seat: TablePlanSeat, table: TablePlanTable): void {
    if (!this.interactive) {
      return;
    }
    event.stopPropagation();
    this.chairDrag = {
      mode: 'move',
      seat,
      sourceTableId: table.id,
      start: { x: event.clientX, y: event.clientY },
      pointer: { x: event.clientX, y: event.clientY },
      moved: false,
      overSide: null,
    };
  }

  protected startChairAdd(event: PointerEvent): void {
    if (!this.interactive || !this.chairPoolDraggable) {
      return;
    }
    event.preventDefault();
    this.chairDrag = {
      mode: 'add',
      seat: null,
      sourceTableId: null,
      start: { x: event.clientX, y: event.clientY },
      pointer: { x: event.clientX, y: event.clientY },
      moved: true,
      overSide: null,
    };
  }

  // ----- Global pointer handlers (track active drag) -----

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (this.markerResize) {
      const scale = this.canvasScale();
      const dx = (event.clientX - this.markerResize.pointerStart.x) / scale;
      const dy = (event.clientY - this.markerResize.pointerStart.y) / scale;
      this.markerResize.current = {
        w: Math.max(TablePlanCanvasComponent.MARKER_MIN_SIZE, this.markerResize.sizeStart.w + dx),
        h: Math.max(TablePlanCanvasComponent.MARKER_MIN_SIZE, this.markerResize.sizeStart.h + dy),
      };
      this.cdr.markForCheck();
      return;
    }

    if (this.markerDrag) {
      const dx = event.clientX - this.markerDrag.pointerStart.x;
      const dy = event.clientY - this.markerDrag.pointerStart.y;
      const scale = this.canvasScale();
      const o = this.outline;
      this.markerDrag.current = {
        x: clampToCanvas(this.markerDrag.markerStart.x + dx / scale, o.width),
        y: clampToCanvas(this.markerDrag.markerStart.y + dy / scale, o.height),
      };
      this.cdr.markForCheck();
      return;
    }

    if (this.markerCatalogueDrag) {
      this.markerCatalogueDrag.pointer = { x: event.clientX, y: event.clientY };
      this.markerCatalogueDrag.overCanvas = this.isOverCanvas(event);
      this.cdr.markForCheck();
      return;
    }

    if (this.chairDrag) {
      this.chairDrag.pointer = { x: event.clientX, y: event.clientY };
      if (!this.chairDrag.moved) {
        const dx = event.clientX - this.chairDrag.start.x;
        const dy = event.clientY - this.chairDrag.start.y;
        if (Math.hypot(dx, dy) > TablePlanCanvasComponent.CHAIR_DRAG_THRESHOLD) {
          this.chairDrag.moved = true;
        }
      }
      this.chairDrag.overSide = this.chairDrag.moved ? this.sideTargetAtPoint(event) : null;
      this.cdr.markForCheck();
      return;
    }

    if (this.dragState && this.dragState.mode === 'move-tile') {
      const dx = event.clientX - this.dragState.pointerStart.x;
      const dy = event.clientY - this.dragState.pointerStart.y;
      const scale = this.canvasScale();
      const o = this.outline;
      this.dragState.current = {
        x: clampToCanvas(this.dragState.tableStart.x + dx / scale, o.width),
        y: clampToCanvas(this.dragState.tableStart.y + dy / scale, o.height),
      };
      // TVG-373: show where it would land and whether it is allowed.
      const moving = this.plan.tables.find((t) => t.id === this.dragState!.tableId);
      this.dragState.placement = moving
        ? this.resolvePlacement(
            moving.tile_type as TileType,
            this.trestlesOf(moving),
            snapToGrid(this.dragState.current.x),
            snapToGrid(this.dragState.current.y),
            moving.rotation,
            sideCountsOf(moving.seats),
            moving.id,
          )
        : null;
      this.cdr.markForCheck();
      return;
    }

    if (this.catalogueDrag) {
      this.catalogueDrag.pointer = { x: event.clientX, y: event.clientY };
      this.catalogueDrag.overCanvas = this.isOverCanvas(event);
      this.cdr.markForCheck();
      return;
    }

    if (this.guestDrag) {
      this.guestDrag.pointer = { x: event.clientX, y: event.clientY };
      if (!this.guestDrag.moved) {
        const dx = event.clientX - this.guestDrag.start.x;
        const dy = event.clientY - this.guestDrag.start.y;
        if (Math.hypot(dx, dy) > TablePlanCanvasComponent.CHAIR_DRAG_THRESHOLD) {
          this.guestDrag.moved = true;
        }
      }
      this.guestDrag.overSeatId = this.guestDrag.moved ? this.seatAtPoint(event) : null;
      this.cdr.markForCheck();
      return;
    }
  }

  @HostListener('document:pointerup', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (this.markerResize) {
      const resize = this.markerResize;
      this.markerResize = null;
      this.persistMarkerResize(
        resize.markerId,
        snapToGrid(resize.current.w),
        snapToGrid(resize.current.h),
      );
      return;
    }

    if (this.markerDrag) {
      const drag = this.markerDrag;
      this.markerDrag = null;
      this.persistMarkerMove(drag.markerId, snapToGrid(drag.current.x), snapToGrid(drag.current.y));
      return;
    }

    if (this.markerCatalogueDrag) {
      const drag = this.markerCatalogueDrag;
      this.markerCatalogueDrag = null;
      if (drag.overCanvas) {
        const svgCoords = this.clientToSvg(event.clientX, event.clientY);
        if (svgCoords) {
          this.placeMarkerFromCatalogue(drag, svgCoords.x, svgCoords.y);
        }
      }
      this.cdr.markForCheck();
      return;
    }

    if (this.chairDrag) {
      const drag = this.chairDrag;
      this.chairDrag = null;
      this.resolveChairDrop(drag, event);
      this.cdr.markForCheck();
      return;
    }

    if (this.dragState && this.dragState.mode === 'move-tile') {
      const tableId = this.dragState.tableId;
      const moved =
        this.dragState.current.x !== this.dragState.tableStart.x ||
        this.dragState.current.y !== this.dragState.tableStart.y;
      const placement = this.dragState.placement;
      this.dragState = null;
      if (moved && placement) {
        this.persistTableMove(tableId, placement);
      }
      this.cdr.markForCheck();
      return;
    }

    if (this.catalogueDrag) {
      const drag = this.catalogueDrag;
      this.catalogueDrag = null;
      if (drag.overCanvas) {
        const svgCoords = this.clientToSvg(event.clientX, event.clientY);
        if (svgCoords) {
          this.placeTileFromCatalogue(drag.tileType, svgCoords.x, svgCoords.y);
        }
      }
      this.cdr.markForCheck();
      return;
    }

    if (this.guestDrag) {
      const drag = this.guestDrag;
      this.guestDrag = null;
      if (drag.moved) {
        if (drag.overSeatId !== null) {
          this.assignGuestToSeat(drag.overSeatId, drag.guest.id);
        }
      } else {
        // A tap with no drag opens the guest's record (TVG-142).
        this.openGuest.emit(drag.guest.id);
      }
      this.cdr.markForCheck();
      return;
    }
  }

  // ----- Selection & deselection -----

  protected onCanvasBackgroundClick(): void {
    this.selectedTableId = null;
    this.selectedMarkerId = null;
  }

  protected selectTable(event: Event, table: TablePlanTable): void {
    event.stopPropagation();
    this.selectedTableId = table.id;
    this.selectedMarkerId = null;
  }

  protected closeSelection(): void {
    this.selectedTableId = null;
  }

  // ----- Toolbar actions -----

  protected rotateSelected(): void {
    const table = this.selectedTable;
    if (!table || !this.interactive) {
      return;
    }
    const next = ((table.rotation + 90) % 360) as 0 | 90 | 180 | 270;
    // Turn about the table centre, keeping the turned table on the same spot.
    const before = rotatedTableSize(table.tile_type as TileType, this.trestlesOf(table), table.rotation);
    const after = rotatedTableSize(table.tile_type as TileType, this.trestlesOf(table), next);
    const x = Math.max(0, snapToGrid(table.position_x + (before.w - after.w) / 2));
    const y = Math.max(0, snapToGrid(table.position_y + (before.h - after.h) / 2));
    if (!this.fits(table.tile_type as TileType, this.trestlesOf(table), x, y, next, sideCountsOf(table.seats), [table.id])) {
      this.flash(table.id);
      this.error.emit('Turning here would hit a wall or another table. Move the table first.');
      return;
    }
    const original = { rotation: table.rotation, x: table.position_x, y: table.position_y };
    table.rotation = next;
    table.position_x = x;
    table.position_y = y;
    this.saving = true;
    this.change.emit();
    this.cdr.markForCheck();
    this.tablePlanService
      .updateTable(this.eventId, table.id, { rotation: next, position_x: x, position_y: y })
      .subscribe({
        next: () => {
          this.saving = false;
        },
        error: (err) => {
          table.rotation = original.rotation;
          table.position_x = original.x;
          table.position_y = original.y;
          this.change.emit();
          this.flash(table.id);
          this.fail(this.fitError(err, 'Failed to rotate table.'));
        },
      });
  }

  /**
   * TVG-373: mark the selected table as a top table, or not. A plan may hold
   * any number of top tables, so the others are left alone.
   */
  protected toggleTopTable(): void {
    const table = this.selectedTable;
    if (!table || !this.interactive) {
      return;
    }
    const makeTop = !table.is_top_table;
    table.is_top_table = makeTop;
    this.saving = true;
    this.change.emit();
    this.cdr.markForCheck();
    this.tablePlanService.updateTable(this.eventId, table.id, { is_top_table: makeTop }).subscribe({
      next: () => {
        this.saving = false;
      },
      error: () => {
        table.is_top_table = !makeTop;
        this.change.emit();
        this.fail('Failed to change the top table.');
      },
    });
  }

  /** TVG-373: the selected table can be lengthened or shortened by a trestle. */
  protected canResize(table: TablePlanTable): boolean {
    return isRun(table.tile_type as TileType);
  }

  protected lengthenSelected(): void {
    const table = this.selectedTable;
    if (table) {
      this.resizeRun(table, this.trestlesOf(table) + 1);
    }
  }

  protected shortenSelected(): void {
    const table = this.selectedTable;
    if (table && this.trestlesOf(table) > 1) {
      this.resizeRun(table, this.trestlesOf(table) - 1);
    }
  }

  private resizeRun(table: TablePlanTable, trestles: number): void {
    if (!this.interactive || table.id < 0) {
      return;
    }
    const shortening = trestles < this.trestlesOf(table);
    if (shortening) {
      // Seats come off the far end, empty ones first; warn when a guest would
      // have to give up their chair.
      const removePerSide = this.furniture.seatsPerSide + 1;
      const atRisk = this.seatsLostOnShorten(table, removePerSide);
      if (atRisk > 0 && !confirm(`Shortening this table returns ${atRisk} seated guest(s) to the guest list. Continue?`)) {
        return;
      }
    } else {
      const counts = sideCountsOf(table.seats);
      const grown = this.lengthenedSpot(table);
      for (const side of this.seatedLong(table)) {
        counts[side] += this.furniture.seatsPerSide + 1;
      }
      if (!grown || !this.fits(table.tile_type as TileType, trestles, grown.x, grown.y, table.rotation, counts, [table.id])) {
        this.flash(table.id);
        this.error.emit('There is no room to add a trestle at the end of this table.');
        return;
      }
    }
    this.saving = true;
    this.cdr.markForCheck();
    this.tablePlanService.updateTable(this.eventId, table.id, { trestle_count: trestles }).subscribe({
      next: (res) => {
        this.saving = false;
        this.reconcileTables(res.data);
        this.change.emit();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.flash(table.id);
        this.fail(this.fitError(err, 'Failed to change the table length.'));
      },
    });
  }

  private seatedLong(table: TablePlanTable): Side[] {
    const type = table.tile_type as TileType;
    const sides = isVertical(type) ? (['right', 'left'] as Side[]) : (['top', 'bottom'] as Side[]);
    const seated: Side[] = type === 'single_3' ? ['top'] : type === 'single_3_below' ? ['bottom'] : type === 'single_3_vertical' ? ['left'] : sides;
    return sides.filter((s) => seated.includes(s));
  }

  /** Seated guests who would lose their chair when the run loses a trestle. */
  private seatsLostOnShorten(table: TablePlanTable, perSide: number): number {
    let lost = 0;
    for (const side of this.seatedLong(table)) {
      const seats = table.seats.filter((s) => s.side === side);
      const empty = seats.filter((s) => !s.event_guest_id).length;
      lost += Math.max(0, Math.min(perSide, seats.length) - empty);
    }
    return lost;
  }

  /** Where a run's corner moves to when it gains a trestle at its far end. */
  private lengthenedSpot(table: TablePlanTable): { x: number; y: number } | null {
    const axis = worldAxis(isVertical(table.tile_type as TileType) ? 'y' : 'x', table.rotation);
    const x = table.position_x - (axis[0] < 0 ? TRESTLE_LONG : 0);
    const y = table.position_y - (axis[1] < 0 ? TRESTLE_LONG : 0);
    return x < 0 || y < 0 ? null : { x, y };
  }

  protected deleteSelected(): void {
    const table = this.selectedTable;
    if (!table || !this.interactive) {
      return;
    }
    if (!confirm(`Delete "${table.label}" and clear its seat assignments?`)) {
      return;
    }
    const idx = this.plan.tables.findIndex((t) => t.id === table.id);
    if (idx < 0) {
      return;
    }
    const removed = this.plan.tables.splice(idx, 1)[0];
    this.selectedTableId = null;
    this.saving = true;
    this.change.emit();
    this.cdr.markForCheck();
    this.tablePlanService.deleteTable(this.eventId, table.id).subscribe({
      next: () => {
        this.saving = false;
      },
      error: () => {
        this.plan.tables.splice(idx, 0, removed);
        this.change.emit();
        this.fail('Failed to delete table.');
      },
    });
  }

  /** TVG-142: clear the whole plan after a confirmation step. */
  protected clearAll(): void {
    if (!this.interactive || !this.hasTables) {
      return;
    }
    if (!confirm('Clear all tables and seat assignments from this plan? This cannot be undone.')) {
      return;
    }
    const previous = this.plan.tables;
    this.plan.tables = [];
    this.selectedTableId = null;
    this.saving = true;
    this.change.emit();
    this.cdr.markForCheck();
    this.tablePlanService.clearAll(this.eventId).subscribe({
      next: (res) => {
        this.saving = false;
        this.plan.tables = res.data.tables;
        this.change.emit();
        this.cdr.markForCheck();
      },
      error: () => {
        this.plan.tables = previous;
        this.change.emit();
        this.fail('Failed to clear the plan.');
      },
    });
  }

  protected unassignSeat(seat: TablePlanSeat, event: Event): void {
    event.stopPropagation();
    if (!this.interactive) {
      return;
    }
    const originalGuestId = seat.event_guest_id;
    const originalGuest = seat.guest;
    seat.event_guest_id = null;
    seat.guest = null;
    this.saving = true;
    this.change.emit();
    this.cdr.markForCheck();
    this.tablePlanService
      .updateSeat(this.eventId, seat.id, { event_guest_id: null })
      .subscribe({
        next: () => {
          this.saving = false;
        },
        error: () => {
          seat.event_guest_id = originalGuestId;
          seat.guest = originalGuest;
          this.change.emit();
          this.fail('Failed to clear seat.');
        },
      });
  }

  // ----- Physical fit, snapping and joining (TVG-142 / TVG-373) -----

  protected trestlesOf(table: TablePlanTable): number {
    return trestleCount(table.tile_type as TileType, table.trestle_count);
  }

  /** Table top + chairs, and the walking space around them, of a placed table. */
  private boxesOf(table: TablePlanTable, x = table.position_x, y = table.position_y): { foot: PlacedBox; space: PlacedBox } {
    const type = table.tile_type as TileType;
    const n = this.trestlesOf(table);
    const counts = sideCountsOf(table.seats);
    return {
      foot: footprint(type, n, x, y, table.rotation, counts),
      space: footprint(type, n, x, y, table.rotation, counts, this.furniture.walkingSpace),
    };
  }

  /**
   * TVG-373: a table fits when its top and chairs are inside the walls and
   * neither it nor any other table reaches into the other's walking space.
   * Fixed features are labels and never block.
   */
  private fits(
    type: TileType,
    trestles: number,
    x: number,
    y: number,
    rotation: number,
    counts: SideCounts,
    ignoreIds: number[] = [],
  ): boolean {
    if (x < 0 || y < 0) {
      return false;
    }
    const foot = footprint(type, trestles, x, y, rotation, counts);
    const space = footprint(type, trestles, x, y, rotation, counts, this.furniture.walkingSpace);
    if (this.outline.polygon.length > 0 && !boxFitsInRoom(foot, this.outline)) {
      return false;
    }
    for (const t of this.placedTables) {
      if (ignoreIds.includes(t.id)) {
        continue;
      }
      const other = this.boxesOf(t);
      if (tooClose(foot, space, other.foot, other.space)) {
        return false;
      }
    }
    return true;
  }

  /** True when a table is too close to another, for the red outline. */
  protected isTooClose(table: TablePlanTable): boolean {
    const own = this.boxesOf(table);
    return this.placedTables.some((t) => {
      if (t.id === table.id) {
        return false;
      }
      const other = this.boxesOf(t);
      return tooClose(own.foot, own.space, other.foot, other.space);
    });
  }

  /**
   * Where a table dropped at (x, y) lands. First a join: a trestle or top
   * table dropped by the end of a run of the same kind lines up to join it, and
   * a single trestle beside another makes a square. Otherwise the table snaps
   * edge to edge onto any side of a neighbour where neither side has chairs.
   * The result says whether that spot is allowed.
   */
  private resolvePlacement(
    type: TileType,
    trestles: number,
    x: number,
    y: number,
    rotation: number,
    counts: SideCounts,
    selfId: number | null,
  ): PlacementResult {
    const join = this.findJoin(type, trestles, x, y, rotation, selfId);
    if (join) {
      return { x: join.x, y: join.y, valid: true, joinTargetId: join.targetId };
    }
    const snapped = this.snapEdges(type, trestles, x, y, rotation, counts, selfId);
    const ignore = selfId !== null ? [selfId] : [];
    return {
      x: snapped.x,
      y: snapped.y,
      valid: this.fits(type, trestles, snapped.x, snapped.y, rotation, counts, ignore),
      joinTargetId: null,
    };
  }

  /** A table this one would join, with the exact spot it lines up at. */
  private findJoin(
    type: TileType,
    trestles: number,
    x: number,
    y: number,
    rotation: number,
    selfId: number | null,
  ): { targetId: number; x: number; y: number } | null {
    if (isSquare(type)) {
      return null;
    }
    const mine = tableBox(type, trestles, x, y, rotation);
    const mineW = mine.x2 - mine.x1;
    const mineH = mine.y2 - mine.y1;
    const near = TablePlanCanvasComponent.SNAP_DISTANCE;
    const reach = TablePlanCanvasComponent.JOIN_DISTANCE;
    for (const t of this.placedTables) {
      if (t.id === selfId || t.id < 0 || t.tile_type !== type) {
        continue;
      }
      const turn = (((rotation - t.rotation) % 360) + 360) % 360;
      if (turn !== 0 && !(turn === 180 && !isTopTableType(type))) {
        continue;
      }
      const box = tableBox(type, this.trestlesOf(t), t.position_x, t.position_y, t.rotation);
      const horizontal = box.x2 - box.x1 >= box.y2 - box.y1 && !(isSquare(type));
      const runAlongX = isVertical(type) ? swapsRun(t.rotation, true) : swapsRun(t.rotation, false);

      // End to end along the run.
      if (runAlongX) {
        if (Math.abs(mine.y1 - box.y1) <= near && mineH === box.y2 - box.y1) {
          if (Math.abs(mine.x1 - box.x2) <= reach) {
            return { targetId: t.id, x: box.x2, y: box.y1 };
          }
          if (Math.abs(mine.x2 - box.x1) <= reach) {
            return { targetId: t.id, x: box.x1 - mineW, y: box.y1 };
          }
        }
      } else if (Math.abs(mine.x1 - box.x1) <= near && mineW === box.x2 - box.x1) {
        if (Math.abs(mine.y1 - box.y2) <= reach) {
          return { targetId: t.id, x: box.x1, y: box.y2 };
        }
        if (Math.abs(mine.y2 - box.y1) <= reach) {
          return { targetId: t.id, x: box.x1, y: box.y1 - mineH };
        }
      }

      // Two single trestles side by side make a square.
      if (type === 'single_6' && trestles === 1 && this.trestlesOf(t) === 1 && horizontal === (mineW >= mineH)) {
        if (runAlongX && Math.abs(mine.x1 - box.x1) <= near) {
          if (Math.abs(mine.y1 - box.y2) <= reach) {
            return { targetId: t.id, x: box.x1, y: box.y2 };
          }
          if (Math.abs(mine.y2 - box.y1) <= reach) {
            return { targetId: t.id, x: box.x1, y: box.y1 - mineH };
          }
        }
        if (!runAlongX && Math.abs(mine.y1 - box.y1) <= near) {
          if (Math.abs(mine.x1 - box.x2) <= reach) {
            return { targetId: t.id, x: box.x2, y: box.y1 };
          }
          if (Math.abs(mine.x2 - box.x1) <= reach) {
            return { targetId: t.id, x: box.x1 - mineW, y: box.y1 };
          }
        }
      }
    }
    return null;
  }

  /**
   * Snap edge to edge: when a side of this table with no chairs comes within
   * snap distance of a neighbour's side with no chairs, butt them together.
   */
  private snapEdges(
    type: TileType,
    trestles: number,
    x: number,
    y: number,
    rotation: number,
    counts: SideCounts,
    selfId: number | null,
  ): { x: number; y: number } {
    const near = TablePlanCanvasComponent.SNAP_DISTANCE;
    const mine = tableBox(type, trestles, x, y, rotation);
    const bare = (c: SideCounts, rot: number, world: Side) =>
      SIDES.every((local) => worldSide(local, rot) !== world || c[local] === 0);
    let nx = x;
    let ny = y;
    for (const t of this.placedTables) {
      if (t.id === selfId) {
        continue;
      }
      const theirs = tableBox(t.tile_type as TileType, this.trestlesOf(t), t.position_x, t.position_y, t.rotation);
      const theirCounts = sideCountsOf(t.seats);
      const overlapY = mine.y1 < theirs.y2 && mine.y2 > theirs.y1;
      const overlapX = mine.x1 < theirs.x2 && mine.x2 > theirs.x1;
      if (overlapY && Math.abs(mine.x1 - theirs.x2) <= near && bare(counts, rotation, 'left') && bare(theirCounts, t.rotation, 'right')) {
        nx = theirs.x2;
      } else if (overlapY && Math.abs(mine.x2 - theirs.x1) <= near && bare(counts, rotation, 'right') && bare(theirCounts, t.rotation, 'left')) {
        nx = theirs.x1 - (mine.x2 - mine.x1);
      } else if (overlapX && Math.abs(mine.y1 - theirs.y2) <= near && bare(counts, rotation, 'top') && bare(theirCounts, t.rotation, 'bottom')) {
        ny = theirs.y2;
      } else if (overlapX && Math.abs(mine.y2 - theirs.y1) <= near && bare(counts, rotation, 'bottom') && bare(theirCounts, t.rotation, 'top')) {
        ny = theirs.y1 - (mine.y2 - mine.y1);
      } else {
        continue;
      }
      // Line the snapped edges up too when they are nearly level.
      if (nx !== x && Math.abs(mine.y1 - theirs.y1) <= near) {
        ny = theirs.y1;
      }
      if (ny !== y && Math.abs(mine.x1 - theirs.x1) <= near) {
        nx = theirs.x1;
      }
      break;
    }
    return { x: nx, y: ny };
  }

  /** Outline a table in red for a moment after a blocked drop. */
  private flash(tableId: number): void {
    this.flashTableId = tableId;
    if (this.flashTimer) {
      clearTimeout(this.flashTimer);
    }
    this.flashTimer = setTimeout(() => {
      this.flashTableId = null;
      this.flashTimer = null;
      this.cdr.markForCheck();
    }, 1200);
    this.cdr.markForCheck();
  }

  // ----- Persistence -----

  private placeTileFromCatalogue(type: TileType, svgX: number, svgY: number): void {
    const counts = defaultSideCounts(type, 1, this.furniture.seatsPerSide);
    const size = tableSize(type, 1);
    const x = Math.max(0, snapToGrid(svgX - size.w / 2));
    const y = Math.max(0, snapToGrid(svgY - size.h / 2));

    const placement = this.resolvePlacement(type, 1, x, y, 0, counts, null);
    if (placement.joinTargetId !== null) {
      this.addAndJoin(type, placement.joinTargetId, placement.x, placement.y);
      return;
    }
    if (!placement.valid) {
      this.error.emit('That spot is outside the walls or inside another table\'s walking space.');
      return;
    }
    const label = this.nextTableLabel();

    const tempTable = this.buildTempTable(type, label, placement.x, placement.y);
    this.plan.tables.push(tempTable);
    this.saving = true;
    this.change.emit();
    this.cdr.markForCheck();

    this.tablePlanService
      .addTable(this.eventId, {
        label,
        tile_type: type,
        position_x: placement.x,
        position_y: placement.y,
        rotation: 0,
      })
      .subscribe({
        next: (res) => {
          this.saving = false;
          this.replaceTempTable(tempTable.id, res.data);
          this.change.emit();
          this.cdr.markForCheck();
        },
        error: (err) => {
          const removeIdx = this.plan.tables.findIndex((t) => t.id === tempTable.id);
          if (removeIdx >= 0) {
            this.plan.tables.splice(removeIdx, 1);
          }
          this.change.emit();
          this.fail(this.fitError(err, 'Failed to place table.'));
        },
      });
  }

  /**
   * TVG-373: a new piece dropped onto the end (or side) of a table joins it.
   * It is created unplaced first, then joined at the lined-up spot; if the
   * join is refused the new piece is removed again.
   */
  private addAndJoin(type: TileType, targetId: number, x: number, y: number): void {
    this.saving = true;
    this.cdr.markForCheck();
    const knownIds = new Set(this.plan.tables.map((t) => t.id));
    this.tablePlanService
      .addTable(this.eventId, { label: this.nextTableLabel(), tile_type: type, position_x: 0, position_y: 0, rotation: 0 })
      .subscribe({
        next: (created) => {
          const fresh = created.data.tables.find((t) => !knownIds.has(t.id));
          if (!fresh) {
            this.reconcileTables(created.data);
            this.saving = false;
            this.change.emit();
            return;
          }
          this.tablePlanService.joinTable(this.eventId, targetId, fresh.id, x, y).subscribe({
            next: (res) => {
              this.saving = false;
              this.reconcileTables(res.data);
              this.selectedTableId = targetId;
              this.change.emit();
              this.cdr.markForCheck();
            },
            error: (err) => {
              this.tablePlanService.deleteTable(this.eventId, fresh.id).subscribe({
                next: (res) => {
                  this.reconcileTables(res.data);
                  this.change.emit();
                  this.cdr.markForCheck();
                },
              });
              this.flash(targetId);
              this.fail(this.fitError(err, 'Those tables could not be joined.'));
            },
          });
        },
        error: (err) => this.fail(this.fitError(err, 'Failed to place table.')),
      });
  }

  private persistTableMove(tableId: number, placement: PlacementResult): void {
    const table = this.plan?.tables.find((t) => t.id === tableId);
    if (!table) {
      return;
    }
    if (placement.joinTargetId !== null) {
      this.joinInto(table, placement);
      return;
    }
    const { x, y } = placement;
    if (table.position_x === x && table.position_y === y) {
      return;
    }
    if (!placement.valid) {
      // TVG-373: blocked — it springs back to its last valid spot, outlined red.
      this.flash(table.id);
      this.error.emit('That spot is outside the walls or inside another table\'s walking space.');
      this.cdr.markForCheck();
      return;
    }
    const origX = table.position_x;
    const origY = table.position_y;
    table.position_x = x;
    table.position_y = y;
    this.saving = true;
    this.change.emit();
    this.cdr.markForCheck();
    this.tablePlanService
      .updateTable(this.eventId, tableId, { position_x: x, position_y: y })
      .subscribe({
        next: () => {
          this.saving = false;
        },
        error: (err) => {
          table.position_x = origX;
          table.position_y = origY;
          this.change.emit();
          this.flash(table.id);
          this.fail(this.fitError(err, 'Failed to move table.'));
        },
      });
  }

  /** TVG-373: a placed table dragged onto another's end (or side) joins it. */
  private joinInto(table: TablePlanTable, placement: PlacementResult): void {
    const targetId = placement.joinTargetId!;
    if (table.id < 0) {
      return;
    }
    this.saving = true;
    this.cdr.markForCheck();
    this.tablePlanService.joinTable(this.eventId, targetId, table.id, placement.x, placement.y).subscribe({
      next: (res) => {
        this.saving = false;
        this.reconcileTables(res.data);
        this.selectedTableId = targetId;
        this.change.emit();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.flash(table.id);
        this.fail(this.fitError(err, 'Those tables could not be joined.'));
      },
    });
  }

  // ----- TVG-216: marker persistence -----

  private placeMarkerFromCatalogue(
    drag: NonNullable<TablePlanCanvasComponent['markerCatalogueDrag']>,
    svgX: number,
    svgY: number,
  ): void {
    const o = this.outline;
    const x = clampToCanvas(snapToGrid(svgX - drag.width / 2), o.width);
    const y = clampToCanvas(snapToGrid(svgY - drag.height / 2), o.height);

    const tempMarker = this.buildTempMarker(drag.type, drag.label, x, y, drag.width, drag.height);
    this.plan.markers.push(tempMarker);
    this.selectedMarkerId = tempMarker.id;
    this.selectedTableId = null;
    this.saving = true;
    this.change.emit();
    this.cdr.markForCheck();

    this.tablePlanService
      .addMarker(this.eventId, {
        type: drag.type,
        label: drag.label,
        position_x: x,
        position_y: y,
        width: drag.width,
        height: drag.height,
      })
      .subscribe({
        next: (res) => {
          this.saving = false;
          this.replaceTempMarker(tempMarker.id, res.data);
          this.change.emit();
          this.cdr.markForCheck();
        },
        error: () => {
          const removeIdx = this.plan.markers.findIndex((m) => m.id === tempMarker.id);
          if (removeIdx >= 0) {
            this.plan.markers.splice(removeIdx, 1);
          }
          this.selectedMarkerId = null;
          this.change.emit();
          this.fail('Failed to place the area.');
        },
      });
  }

  private persistMarkerMove(markerId: number, x: number, y: number): void {
    const marker = this.markers.find((m) => m.id === markerId);
    if (!marker) {
      return;
    }
    if (marker.position_x === x && marker.position_y === y) {
      return;
    }
    const origX = marker.position_x;
    const origY = marker.position_y;
    marker.position_x = x;
    marker.position_y = y;
    this.change.emit();
    this.cdr.markForCheck();
    if (markerId < 0) {
      // The create is still in flight; the drop position it carried wins.
      return;
    }
    this.saving = true;
    this.tablePlanService
      .updateMarker(this.eventId, markerId, { position_x: x, position_y: y })
      .subscribe({
        next: () => {
          this.saving = false;
        },
        error: () => {
          marker.position_x = origX;
          marker.position_y = origY;
          this.change.emit();
          this.fail('Failed to move the area.');
        },
      });
  }

  private persistMarkerResize(markerId: number, width: number, height: number): void {
    const marker = this.markers.find((m) => m.id === markerId);
    if (!marker) {
      return;
    }
    const w = Math.max(TablePlanCanvasComponent.MARKER_MIN_SIZE, width);
    const h = Math.max(TablePlanCanvasComponent.MARKER_MIN_SIZE, height);
    if (marker.width === w && marker.height === h) {
      return;
    }
    const origW = marker.width;
    const origH = marker.height;
    marker.width = w;
    marker.height = h;
    this.change.emit();
    this.cdr.markForCheck();
    if (markerId < 0) {
      return;
    }
    this.saving = true;
    this.tablePlanService
      .updateMarker(this.eventId, markerId, { width: w, height: h })
      .subscribe({
        next: () => {
          this.saving = false;
        },
        error: () => {
          marker.width = origW;
          marker.height = origH;
          this.change.emit();
          this.fail('Failed to resize the area.');
        },
      });
  }

  private buildTempMarker(
    type: TablePlanMarkerType,
    label: string,
    x: number,
    y: number,
    width: number,
    height: number,
  ): TablePlanMarker {
    return {
      id: -Date.now(),
      type,
      label,
      position_x: x,
      position_y: y,
      width,
      height,
      sort_order: this.plan.markers.length + 1,
    };
  }

  private replaceTempMarker(tempId: number, serverPlan: TablePlan): void {
    const tempIdx = this.plan.markers.findIndex((m) => m.id === tempId);
    if (tempIdx < 0) {
      return;
    }
    const knownIds = new Set(this.plan.markers.map((m) => m.id));
    const realMarker = (serverPlan.markers ?? []).find((m) => !knownIds.has(m.id));
    if (realMarker) {
      if (this.selectedMarkerId === tempId) {
        this.selectedMarkerId = realMarker.id;
      }
      this.plan.markers[tempIdx] = realMarker;
    }
  }

  private assignGuestToSeat(seatId: number, guestId: number): void {
    const seat = this.findSeat(seatId);
    if (!seat) {
      return;
    }
    if (seatId < 0) {
      this.error.emit('Drop the new table first, then assign guests.');
      return;
    }
    const guest = this.guests.find((g) => g.id === guestId) ?? null;
    const originalGuestId = seat.event_guest_id;
    const originalGuest = seat.guest;
    const previousSeat = this.findSeatByGuestId(guestId);
    const previousState = previousSeat
      ? { seat: previousSeat, guestId: previousSeat.event_guest_id, guest: previousSeat.guest }
      : null;
    if (previousSeat) {
      previousSeat.event_guest_id = null;
      previousSeat.guest = null;
    }
    seat.event_guest_id = guestId;
    seat.guest = guest;
    this.saving = true;
    this.change.emit();
    this.cdr.markForCheck();
    this.tablePlanService
      .updateSeat(this.eventId, seatId, { event_guest_id: guestId })
      .subscribe({
        next: () => {
          this.saving = false;
        },
        error: () => {
          seat.event_guest_id = originalGuestId;
          seat.guest = originalGuest;
          if (previousState) {
            previousState.seat.event_guest_id = previousState.guestId;
            previousState.seat.guest = previousState.guest;
          }
          this.change.emit();
          this.fail('Failed to assign guest.');
        },
      });
  }

  // ----- Chair drop resolution + persistence (TVG-132) -----

  private resolveChairDrop(
    drag: NonNullable<TablePlanCanvasComponent['chairDrag']>,
    event: PointerEvent,
  ): void {
    if (!drag.moved) {
      return;
    }

    if (drag.mode === 'add') {
      if (drag.overSide) {
        this.addChair(drag.overSide);
      }
      return;
    }

    const seat = drag.seat;
    if (!seat) {
      return;
    }

    if (drag.overSide) {
      const target = drag.overSide;
      const sameSpot =
        target.tableId === drag.sourceTableId &&
        target.side === seat.side &&
        (target.index === seat.position_index || target.index === seat.position_index + 1);
      if (!sameSpot) {
        this.moveChair(seat, target);
      }
      return;
    }

    // Dropped off its table: the chair is removed.
    const sourceTable = this.plan?.tables.find((t) => t.id === drag.sourceTableId) ?? null;
    const svgPoint = this.clientToSvg(event.clientX, event.clientY);
    if (sourceTable && svgPoint && !this.isPointInsideTable(svgPoint, sourceTable)) {
      this.removeChair(seat);
    }
  }

  private addChair(target: SideTarget): void {
    if (target.tableId < 0) {
      this.error.emit('Drop the new table first, then add chairs.');
      return;
    }
    this.saving = true;
    this.cdr.markForCheck();
    this.tablePlanService.addChair(this.eventId, target.tableId, target.side, target.index).subscribe({
      next: (res) => {
        this.saving = false;
        this.reconcileTables(res.data);
        this.change.emit();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.flash(target.tableId);
        this.fail(this.chairError(err, 'Failed to add chair.'));
      },
    });
  }

  private moveChair(seat: TablePlanSeat, target: SideTarget): void {
    if (seat.id < 0 || target.tableId < 0) {
      this.error.emit('Drop the new table first, then move chairs.');
      return;
    }
    // Moving later along the same side: the chair's own place closes up first.
    const sameSide = seat.side === target.side && this.tableOfSeat(seat.id)?.id === target.tableId;
    const index = sameSide && target.index > seat.position_index ? target.index - 1 : target.index;
    this.saving = true;
    this.cdr.markForCheck();
    this.tablePlanService.moveChair(this.eventId, seat.id, target.tableId, target.side, index).subscribe({
      next: (res) => {
        this.saving = false;
        this.reconcileTables(res.data);
        this.change.emit();
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.flash(target.tableId);
        this.fail(this.chairError(err, 'Failed to move chair.'));
      },
    });
  }

  protected removeChair(seat: TablePlanSeat, event?: Event): void {
    event?.stopPropagation();
    if (seat.id < 0 || !this.interactive) {
      return;
    }
    if (seat.guest && !confirm(this.releaseGuestMessage(seat))) {
      return;
    }
    this.hoveredSeatId = null;
    this.saving = true;
    this.cdr.markForCheck();
    this.tablePlanService.removeChair(this.eventId, seat.id).subscribe({
      next: (res) => {
        this.saving = false;
        this.reconcileTables(res.data);
        this.change.emit();
        this.cdr.markForCheck();
      },
      error: (err) => this.fail(this.chairError(err, 'Failed to remove chair.')),
    });
  }

  /** TVG-373: make a chair a high chair, or a normal chair again. */
  protected toggleHighChair(seat: TablePlanSeat, event: Event): void {
    event.stopPropagation();
    if (seat.id < 0 || !this.interactive) {
      return;
    }
    const next = !seat.is_high_chair;
    seat.is_high_chair = next;
    this.saving = true;
    this.change.emit();
    this.cdr.markForCheck();
    this.tablePlanService.updateSeat(this.eventId, seat.id, { event_guest_id: seat.event_guest_id, is_high_chair: next }).subscribe({
      next: () => {
        this.saving = false;
      },
      error: () => {
        seat.is_high_chair = !next;
        this.change.emit();
        this.fail('Failed to change the high chair.');
      },
    });
  }

  private tableOfSeat(seatId: number): TablePlanTable | null {
    return this.plan?.tables.find((t) => t.seats.some((s) => s.id === seatId)) ?? null;
  }

  private releaseGuestMessage(seat: TablePlanSeat): string {
    const name = seat.guest?.full_name ?? 'This guest';
    return `Removing this chair returns ${name} to the unassigned guest list. Continue?`;
  }

  private chairError(err: unknown, fallback: string): string {
    return this.fitError(err, fallback);
  }

  /** Surface the server's placement/fit rejection message when present. */
  private fitError(err: unknown, fallback: string): string {
    const errors = (err as { error?: { errors?: Record<string, string[]> } })?.error?.errors;
    const first = errors ? Object.values(errors)[0]?.[0] : undefined;
    return first ?? fallback;
  }

  private reconcileTables(serverPlan: TablePlan): void {
    if (!this.plan) {
      return;
    }
    const byId = new Map(serverPlan.tables.map((t) => [t.id, t]));
    this.plan.tables = this.plan.tables
      .filter((t) => t.id < 0 || byId.has(t.id))
      .map((t) => (t.id < 0 ? t : byId.get(t.id)!));
    for (const st of serverPlan.tables) {
      if (!this.plan.tables.some((t) => t.id === st.id)) {
        this.plan.tables.push(st);
      }
    }
  }

  private buildTempTable(
    type: TileType,
    label: string,
    x: number,
    y: number,
  ): TablePlanTable {
    // TVG-373: a placed table carries its default chairs, side by side.
    const counts = defaultSideCounts(type, 1, this.furniture.seatsPerSide);
    const baseId = -Date.now();
    const seats: TablePlanSeat[] = [];
    for (const side of SIDES) {
      for (let i = 0; i < counts[side]; i++) {
        seats.push({
          id: baseId - seats.length - 1,
          seat_number: seats.length + 1,
          position_index: i,
          side,
          is_high_chair: false,
          event_guest_id: null,
          guest: null,
        });
      }
    }
    return {
      id: baseId,
      label,
      seat_count: seats.length,
      sort_order: this.plan.tables.length + 1,
      tile_type: type as TablePlanTileType,
      trestle_count: 1,
      position_x: x,
      position_y: y,
      rotation: 0,
      is_top_table: isTopTableType(type),
      seats,
    };
  }

  private replaceTempTable(tempId: number, serverPlan: TablePlan): void {
    const tempIdx = this.plan.tables.findIndex((t) => t.id === tempId);
    if (tempIdx < 0) {
      return;
    }
    const knownIds = new Set(this.plan.tables.map((t) => t.id));
    const realTable = serverPlan.tables.find((t) => !knownIds.has(t.id));
    if (realTable) {
      this.plan.tables[tempIdx] = realTable;
    }
  }

  private findSeat(seatId: number): TablePlanSeat | null {
    for (const t of this.plan?.tables ?? []) {
      const s = t.seats.find((seat) => seat.id === seatId);
      if (s) {
        return s;
      }
    }
    return null;
  }

  private findSeatByGuestId(guestId: number): TablePlanSeat | null {
    for (const t of this.plan?.tables ?? []) {
      const s = t.seats.find((seat) => seat.event_guest_id === guestId);
      if (s) {
        return s;
      }
    }
    return null;
  }

  private nextTableLabel(): string {
    const existing = (this.plan?.tables ?? []).map((t) => t.label);
    let n = (this.plan?.tables.length ?? 0) + 1;
    while (existing.includes(`Table ${n}`)) {
      n++;
    }
    return `Table ${n}`;
  }

  private fail(message: string): void {
    this.saving = false;
    this.error.emit(message);
    this.cdr.markForCheck();
  }

  // ----- Coordinate helpers -----

  /**
   * TVG-145: the SVG box no longer matches the room's aspect ratio. Since the
   * canvas became true-to-scale (TVG-142) the room is meet-scaled inside a
   * fixed-height box (so a tall room like the Great Barn fits without growing
   * the page), which letterboxes the drawing. Projecting pointer/screen
   * coordinates therefore has to use the single meet scale plus the centring
   * offsets, not a bare width ratio — otherwise drags and the toolbar land in
   * the wrong place. Reduces to the old behaviour when there is no letterbox.
   */
  private svgProjection(): { rect: DOMRect; scale: number; offsetX: number; offsetY: number } | null {
    const svg = this.svgRef?.nativeElement;
    if (!svg) {
      return null;
    }
    const rect = svg.getBoundingClientRect();
    const scale = Math.min(rect.width / this.viewBoxWidth, rect.height / this.viewBoxHeight) || 1;
    const offsetX = (rect.width - this.viewBoxWidth * scale) / 2;
    const offsetY = (rect.height - this.viewBoxHeight * scale) / 2;
    return { rect, scale, offsetX, offsetY };
  }

  /** Pixel-per-canvas-unit ratio of the rendered SVG. */
  private canvasScale(): number {
    return this.svgProjection()?.scale ?? 1;
  }

  private isOverCanvas(event: PointerEvent): boolean {
    const svg = this.svgRef?.nativeElement;
    if (!svg) {
      return false;
    }
    const rect = svg.getBoundingClientRect();
    return (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    );
  }

  private clientToSvg(clientX: number, clientY: number): { x: number; y: number } | null {
    const projection = this.svgProjection();
    if (!projection) {
      return null;
    }
    const { rect, scale, offsetX, offsetY } = projection;
    return {
      x: (clientX - rect.left - offsetX) / scale - this.MARGIN,
      y: (clientY - rect.top - offsetY) / scale - this.MARGIN,
    };
  }

  private seatAtPoint(event: PointerEvent): number | null {
    const elements = document.elementsFromPoint(event.clientX, event.clientY);
    for (const el of elements) {
      const seatAttr = (el as HTMLElement | SVGElement).getAttribute?.('data-seat-id');
      if (seatAttr) {
        return Number(seatAttr);
      }
      const parent = (el as HTMLElement | SVGElement).closest?.('[data-seat-id]');
      if (parent) {
        return Number(parent.getAttribute('data-seat-id'));
      }
    }
    return null;
  }

  private isPointInsideTable(point: { x: number; y: number }, table: TablePlanTable): boolean {
    const box = this.boxesOf(table).foot;
    return point.x >= box.x1 && point.x <= box.x2 && point.y >= box.y1 && point.y <= box.y2;
  }

  /**
   * Where a dragged chair would land: the side of a table under the pointer
   * (its chair strip), and the place along it, read from the pointer.
   */
  private sideTargetAtPoint(event: PointerEvent): SideTarget | null {
    const elements = document.elementsFromPoint(event.clientX, event.clientY);
    for (const el of elements) {
      const attr =
        (el as HTMLElement | SVGElement).getAttribute?.('data-side-slot') ??
        (el as HTMLElement | SVGElement).closest?.('[data-side-slot]')?.getAttribute('data-side-slot') ??
        null;
      if (!attr) {
        continue;
      }
      const [idText, side] = attr.split(':');
      const table = this.plan.tables.find((t) => t.id === Number(idText));
      const point = this.clientToSvg(event.clientX, event.clientY);
      if (!table || !point) {
        return null;
      }
      const type = table.tile_type as TileType;
      const n = this.trestlesOf(table);
      const local = toLocal(type, n, table.position_x, table.position_y, table.rotation, point.x, point.y);
      const { w, h } = tableSize(type, n);
      // Fraction along the side in clockwise order.
      const along =
        side === 'top' ? local.x / w : side === 'right' ? local.y / h : side === 'bottom' ? 1 - local.x / w : 1 - local.y / h;
      const count = table.seats.filter((s) => s.side === side).length;
      const index = Math.max(0, Math.min(count, Math.round(along * count)));
      return { tableId: table.id, side: side as Side, index };
    }
    return null;
  }

  /** The chairs of a table, laid out evenly along their sides. */
  protected chairsFor(table: TablePlanTable): PlacedChair[] {
    const type = table.tile_type as TileType;
    const n = this.trestlesOf(table);
    const counts = sideCountsOf(table.seats);
    return table.seats.map((seat) => {
      const side = (seat.side ?? 'top') as Side;
      const high = this.seatIsHighChair(seat);
      const rect = chairRect(type, n, side, seat.position_index, counts[side], high);
      return { seat, ...rect, high };
    });
  }

  protected trestlesFor(table: TablePlanTable) {
    return trestleRects(table.tile_type as TileType, this.trestlesOf(table));
  }

  protected tableSizeFor(table: TablePlanTable) {
    return tableSize(table.tile_type as TileType, this.trestlesOf(table));
  }

  /**
   * Chair drop strips shown on every side a chair may go on while a chair is
   * being dragged. A full top table end, or a side butted against another
   * table, shows no strip.
   */
  protected sideTargetsFor(table: TablePlanTable): Array<{ side: Side; x: number; y: number; w: number; h: number }> {
    const type = table.tile_type as TileType;
    const { w, h } = this.tableSizeFor(table);
    const counts = sideCountsOf(table.seats);
    const d = CHAIR_D;
    const strips: Record<Side, { x: number; y: number; w: number; h: number }> = {
      top: { x: 0, y: -d, w, h: d },
      right: { x: w, y: 0, w: d, h },
      bottom: { x: 0, y: h, w, h: d },
      left: { x: -d, y: 0, w: d, h },
    };
    return allowedSides(type)
      .filter((side) => counts[side] < sideMax(type, side))
      .map((side) => ({ side, ...strips[side] }));
  }

  protected get showSideTargets(): boolean {
    return !!this.chairDrag?.moved;
  }

  protected isSideTarget(tableId: number, side: Side): boolean {
    return this.chairDrag?.overSide?.tableId === tableId && this.chairDrag?.overSide?.side === side;
  }

  protected get chairPool(): number | null {
    return this.summary?.chair_pool ?? null;
  }

  protected get chairPoolEnforced(): boolean {
    return !!this.summary?.chair_pool_enforced;
  }

  protected get chairPoolDraggable(): boolean {
    return this.chairPoolEnforced ? (this.chairPool ?? 0) > 0 : true;
  }

  protected isTopTable(table: TablePlanTable): boolean {
    return !!table.is_top_table;
  }

  /** Where a table is drawn, following an in-flight drag. */
  private livePosition(table: TablePlanTable): { x: number; y: number } {
    if (this.dragState && this.dragState.mode === 'move-tile' && this.dragState.tableId === table.id) {
      const p = this.dragState.placement;
      return p ? { x: p.x, y: p.y } : this.dragState.current;
    }
    return { x: table.position_x, y: table.position_y };
  }

  protected tileTransform(table: TablePlanTable): string {
    const { x, y } = this.livePosition(table);
    return tableTransform(table.tile_type as TileType, this.trestlesOf(table), x, y, table.rotation);
  }

  /** The walking space drawn round a table while it is dragged or selected. */
  protected spaceBoxFor(table: TablePlanTable): PlacedBox {
    const { x, y } = this.livePosition(table);
    return this.boxesOf(table, x, y).space;
  }

  /** The table-and-chairs box, for the red outline. */
  protected footBoxFor(table: TablePlanTable): PlacedBox {
    const { x, y } = this.livePosition(table);
    return this.boxesOf(table, x, y).foot;
  }

  protected isDragging(table: TablePlanTable): boolean {
    return this.dragState?.tableId === table.id;
  }

  /** Red outline: a blocked drop just now, a drag that would be blocked, or too close. */
  protected isFlagged(table: TablePlanTable): boolean {
    if (this.flashTableId === table.id) {
      return true;
    }
    if (this.isDragging(table)) {
      return this.dragState?.placement ? !this.dragState.placement.valid : false;
    }
    return this.isTooClose(table) || this.isOutsideRoom(table);
  }

  protected toolbarPosition(table: TablePlanTable): { left: number; top: number } | null {
    const projection = this.svgProjection();
    if (!projection) {
      return null;
    }
    const { rect, scale, offsetX, offsetY } = projection;
    const box = this.boxesOf(table).foot;
    // viewBox origin is offset by -MARGIN, so add MARGIN back when projecting.
    // offsetX/offsetY account for the meet-scale letterboxing (TVG-145).
    return {
      left: rect.left + offsetX + (box.x2 + this.MARGIN) * scale + 8,
      top: rect.top + offsetY + (box.y1 + this.MARGIN) * scale,
    };
  }

  protected isOutsideRoom(table: TablePlanTable): boolean {
    return this.outline.polygon.length > 0 && !boxFitsInRoom(this.boxesOf(table).foot, this.outline);
  }

  // ----- TVG-373: drawing helpers -----

  /** A Furniture panel thumbnail: the piece with its default chairs. */
  protected catalogueThumb(type: TileType): { viewBox: string; piece: ReturnType<typeof previewPiece> } {
    const counts = defaultSideCounts(type, 1, this.furniture.seatsPerSide);
    const box = footprint(type, 1, 0, 0, 0, counts);
    const pad = 6;
    return {
      viewBox: `${box.x1 - pad} ${box.y1 - pad} ${box.x2 - box.x1 + 2 * pad} ${box.y2 - box.y1 + 2 * pad}`,
      piece: previewPiece({ tile_type: type, trestle_count: 1, sides: counts, seat_count: 0, position_x: 0, position_y: 0, rotation: 0 }),
    };
  }

  /** The two short jambs drawn at the ends of a wall opening. */
  protected jambsFor(wall: RoomOutline['walls'][number]): Array<{ x1: number; y1: number; x2: number; y2: number }> {
    const dx = wall.x2 - wall.x1;
    const dy = wall.y2 - wall.y1;
    const len = Math.hypot(dx, dy) || 1;
    const nx = (-dy / len) * 14;
    const ny = (dx / len) * 14;
    return [
      { x1: wall.x1 - nx, y1: wall.y1 - ny, x2: wall.x1 + nx, y2: wall.y1 + ny },
      { x1: wall.x2 - nx, y1: wall.y2 - ny, x2: wall.x2 + nx, y2: wall.y2 + ny },
    ];
  }

  /** Where a chair's hover menu sits: just outside the chair, away from the table. */
  protected chairMenuPosition(chair: PlacedChair, size: { w: number; h: number }, buttons: number): { x: number; y: number } {
    if (chair.orientation === 'horizontal') {
      const above = chair.cy < size.h / 2;
      // The buttons overlap the chair edge so the pointer never leaves the
      // chair on its way to them (which would close the menu).
      return { x: chair.cx - 56, y: above ? chair.y - 16 : chair.y + chair.h + 16 };
    }
    const left = chair.cx < size.w / 2;
    return { x: left ? chair.x - 16 - (buttons - 1) * 56 : chair.x + chair.w + 16, y: chair.cy };
  }

  protected guestHasDietary(guest: TablePlanGuest): boolean {
    if (guest.has_dietary_requirement !== undefined) {
      return !!guest.has_dietary_requirement;
    }
    return !!guest.has_dietary && !guest.has_allergy;
  }

  // ----- TVG-373: chair colours and marks -----

  private guestFor(seat: TablePlanSeat): TablePlanGuest | null {
    if (!seat.guest) {
      return null;
    }
    return this.guests.find((g) => g.id === seat.guest!.id) ?? seat.guest;
  }

  /** Allergy: an allergen or an airborne allergy on the seated guest. */
  protected seatHasAllergy(seat: TablePlanSeat): boolean {
    return !!this.guestFor(seat)?.has_allergy;
  }

  /** Dietary: any other dietary need on the seated guest. */
  protected seatHasDietary(seat: TablePlanSeat): boolean {
    const guest = this.guestFor(seat);
    if (!guest) {
      return false;
    }
    if (guest.has_dietary_requirement !== undefined) {
      return !!guest.has_dietary_requirement;
    }
    return !!guest.has_dietary && !guest.has_allergy;
  }

  /** A high chair: set on the chair, or needed by the guest sitting in it. */
  protected seatIsHighChair(seat: TablePlanSeat): boolean {
    return !!seat.is_high_chair || !!this.guestFor(seat)?.requires_high_chair;
  }

  protected seatFill(seat: TablePlanSeat): string {
    return seat.guest ? this.PLAN_COLOURS.seatedChair : this.PLAN_COLOURS.emptyChair;
  }

  protected seatStroke(seat: TablePlanSeat): string {
    if (this.seatIsHighChair(seat)) {
      return this.PLAN_COLOURS.highChair;
    }
    return seat.guest ? this.PLAN_COLOURS.seatedChair : this.PLAN_COLOURS.emptyChairEdge;
  }

  protected seatTitle(seat: TablePlanSeat): string {
    const parts: string[] = [seat.guest ? seat.guest.full_name : `Empty seat ${seat.seat_number}`];
    if (this.seatIsHighChair(seat)) {
      parts.push('high chair');
    }
    if (this.seatHasDietary(seat)) {
      parts.push('dietary');
    }
    if (this.seatHasAllergy(seat)) {
      parts.push('allergy');
    }
    return parts.join(' · ');
  }
}

/** True when a run lies along the room's x axis at this rotation. */
function swapsRun(rotation: number, vertical: boolean): boolean {
  const turned = ((rotation % 360) + 360) % 360 === 90 || ((rotation % 360) + 360) % 360 === 270;
  return vertical ? turned : !turned;
}
